// Single compact toolbar: channel mutes (ding/desktop live on Firefly icon row).
import { useState } from "react";
import { orgTeamChannelId } from "@/lib/messages/chatActivityBridgeModel";
import { ActivityDockAlertIcons } from "@/components/activity/ActivityDockAlertIcons";

export type WorkplaceMessengerToolbarProps = {
  compact?: boolean;
  orgId?: string;
  bridgeChannelSlugs?: string[];
  mutedChannelIds?: string[];
  onBridgeMute?: (channelSlug: string, muted: boolean) => Promise<void>;
  /** Hide ding/desktop (already on Firefly primary tab row). */
  hideAlertControls?: boolean;
};

export function WorkplaceMessengerToolbar({
  compact = false,
  orgId,
  bridgeChannelSlugs = [],
  mutedChannelIds = [],
  onBridgeMute,
  hideAlertControls = false,
}: WorkplaceMessengerToolbarProps) {
  const [mutesOpen, setMutesOpen] = useState(false);

  const textSize = compact ? "text-[10px]" : "text-[11px]";
  const hasMutes = Boolean(orgId && bridgeChannelSlugs.length > 0 && onBridgeMute);

  if (!hasMutes && hideAlertControls) return null;

  return (
    <div
      className={`relative flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-teal-200/80 bg-white/70 px-2 py-1 dark:border-teal-900 dark:bg-zinc-900/60 ${textSize}`}
    >
      {hasMutes ? (
        <div className="relative min-w-0">
          <button
            type="button"
            onClick={() => setMutesOpen((v) => !v)}
            className="font-medium text-teal-900 hover:underline dark:text-teal-100"
            aria-expanded={mutesOpen}
          >
            Channel mutes
          </button>
          {mutesOpen ? (
            <div className="absolute left-0 top-full z-20 mt-1 min-w-[11rem] rounded-md border border-teal-200 bg-white p-2 shadow-lg dark:border-teal-800 dark:bg-zinc-900">
              <ul className="space-y-1">
                {bridgeChannelSlugs.map((slug) => {
                  const channelKey = orgTeamChannelId(orgId!, slug);
                  const muted = mutedChannelIds.includes(channelKey);
                  return (
                    <li key={slug} className="flex items-center justify-between gap-2">
                      <span className="truncate text-gray-700 dark:text-gray-200">#{slug}</span>
                      <button
                        type="button"
                        onClick={() => void onBridgeMute!(slug, !muted)}
                        className="shrink-0 text-teal-800 hover:underline dark:text-teal-200"
                      >
                        {muted ? "Unmute" : "Mute"}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      ) : (
        <span className="text-gray-400 dark:text-gray-500">Alerts</span>
      )}

      {!hideAlertControls ? <ActivityDockAlertIcons className="ml-auto" /> : null}
    </div>
  );
}
